/**
 * IdleSecurityProvider — wraps the authenticated app. Drives the inactivity
 * auto-lock:
 *   • 3 min idle  → show LockScreen + audit locked_at / inactive_at
 *   • 5 min idle  → pause the active work session + audit pause_started_at
 *   • unlock      → audit unlocked_at, resume tracking
 *
 * Thresholds come from REACT_APP_LOCK_IDLE_MS / REACT_APP_PAUSE_IDLE_MS
 * (defaults 180000 / 300000). Works in the browser and inside Electron (DOM
 * activity fires in the renderer); also honours the native powerMonitor
 * `onIdleDetected` signal when running in the desktop shell.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import useIdleTimer from '../../hooks/useIdleTimer';
import LockScreen from './LockScreen';
import { authFetch } from '../../services/apiServices';
import globals from '../../services/globals';

const LOCK_MS = Number(process.env.REACT_APP_LOCK_IDLE_MS) || 180000;   // 3 min
const PAUSE_MS = Number(process.env.REACT_APP_PAUSE_IDLE_MS) || 300000; // 5 min

function post(path) {
  return authFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ surface: typeof window !== 'undefined' && window.electronAPI ? 'desktop' : 'web' }),
  }).catch(() => {});
}

// Auto-lock is a desktop-shell feature only; the web app never locks.
const IS_DESKTOP = typeof window !== 'undefined' && !!window.electronAPI;

export default function IdleSecurityProvider({ children }) {
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  lockedRef.current = locked;

  const user = globals().getLogedInUser?.();

  const handleLock = useCallback(() => {
    if (lockedRef.current) return;
    setLocked(true);
    post('/api/security/inactive');
    post('/api/security/lock');
  }, []);

  const handlePause = useCallback(() => {
    post('/api/security/pause');
  }, []);

  const { reset } = useIdleTimer({
    lockAfterMs: LOCK_MS,
    pauseAfterMs: PAUSE_MS,
    trackActivity: !locked, // once locked, stop resetting so pause still fires at 5 min
    enabled: IS_DESKTOP,    // web never auto-locks
    onLock: handleLock,
    onPause: handlePause,
  });

  const handleUnlocked = useCallback(() => {
    post('/api/security/unlock');
    reset();
    setLocked(false);
  }, [reset]);

  // Native desktop reinforcement: powerMonitor idle → lock immediately.
  useEffect(() => {
    const api = typeof window !== 'undefined' ? window.electronAPI : null;
    if (!api?.onIdleDetected) return;
    api.onIdleDetected(() => handleLock());
  }, [handleLock]);

  return (
    <>
      {children}
      {locked && <LockScreen user={user} onUnlocked={handleUnlocked} />}
    </>
  );
}
